/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, EventData, Event } from '../types';
import {
  getDailyBriefing,
  getEvent,
  EventConflict,
  BriefingEvent,
} from '../services';
import { useEventsStore, useSettingsStore, useUserStore } from '../stores';
import {
  IncomingShare,
  MonthCluster,
  MonthWeek,
  NextTripResponse,
  TodayActivity,
  WeekDay,
  checkinItineraryItem,
  getIncomingShares,
  getMonthSummary,
  getNextTrip,
  getTodayActivities,
  getTodayGaps,
  getWeekActivities,
} from '../services/plannerApi';
import { fetchApi } from '../services/apiBase';
import { safeAsync } from '../utils/errorUtils';
import { useChat } from '../hooks/useChat';
import type { ChatMessage } from '../hooks/useChat';
import { useSchedule, clearScheduleCache } from '../hooks/useSchedule';
import type { ScheduleEvent } from '../hooks/useSchedule';
import ChatOverlay from '../components/chat/ChatOverlay';
import PlanChatOverlay from '../components/chat/PlanChatOverlay';
import FirstScreen from '../components/starters/FirstScreen';
import { usePlanChat } from '../hooks/usePlanChat';
import { newUuid } from '../utils/uuid';
import TodayTimeline from '../components/today/TodayTimeline';
import DepartureBoard from '../components/today/DepartureBoard';
import UnifiedMap from '../components/UnifiedMap';
import { usePodToday } from '../hooks/usePodToday';
import AttendanceRow from '../components/pods/today/AttendanceRow';
import UpdateComposer from '../components/pods/today/UpdateComposer';
import DayFeedDrawer from '../components/pods/today/DayFeedDrawer';
import PodTodayOnboarding from '../components/pods/today/PodTodayOnboarding';
import EventEditModal from '../components/EventEditModal';
import { Header } from '../components/home';
import { BottomNav } from '../components/navigation';
import './MobileHome.css';

interface MobileHomeProps {
  user: User | null;
}

/** Format a Date to YYYY-MM-DD using local timezone (not UTC). */
function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MobileHome: React.FC<MobileHomeProps> = ({ user }) => {
  const navigate = useNavigate();
  const fetchConflicts = useEventsStore((s) => s.fetchConflicts);
  const eventsStoreError = useEventsStore((s) => s.error);

  // Schedule hook
  const schedule = useSchedule();
  const hasLoadedOnce = useRef(false);

  // Chat hook
  const chat = useChat({
    onNavigate: (path) => navigate(path),
    onDataRefresh: () => loadData(),
    context: 'daily',
  });

  // Anonymous plan chat for unauthed users
  const planChat = usePlanChat(null, 'plan_create');
  const [isPlanChatOpen, setIsPlanChatOpen] = useState(false);

  // Notifications panel state

  const [gaps, setGaps] = useState<Array<{ start: string; end: string; duration: number; emoji: string; title: string; suggestion: string }>>([]);

  // Scope toggle state (Today/Week/Month)
  const [scope, setScope] = useState<'today' | 'week' | 'month'>('today');
  const [offset, setOffset] = useState(0);
  const [weekData, setWeekData] = useState<WeekDay[]>([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [monthData, setMonthData] = useState<MonthWeek[]>([]);
  const [monthClusters, setMonthClusters] = useState<MonthCluster[]>([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [discoverDestination, setDiscoverDestination] = useState<string | null>(null);
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [discoverPlans, setDiscoverPlans] = useState<Array<{ id: number; title: string; slug: string; destination: string; used_count: number; creator_name: string; source?: string }>>([]);
  const [incomingShares, setIncomingShares] = useState<IncomingShare[]>([]);
  const [todayWeather, setTodayWeather] = useState<{ icon: string; high: number | null; low: number | null; description: string } | null>(null);
  const [activityStatuses, setActivityStatuses] = useState<Map<string, string>>(new Map());
  const [todayPlanActivities, setTodayPlanActivities] = useState<TodayActivity[]>([]);
  const [viewedDayEvents, setViewedDayEvents] = useState<ScheduleEvent[]>([]);
  const [updateComposerTarget, setUpdateComposerTarget] = useState<{ planId: number; itemId: number; name: string } | null>(null);

  // The between-trips departure board (spec §22.5, Phase 4). Read from the ONE
  // server-side next-trip selector rather than re-deriving "soonest departure"
  // here. What stood in this spot was `upcomingTrip` state, set from the
  // briefing's trip_mode on every load and rendered by nothing at all.
  // Stored WITH the account it describes. Clearing in an effect was not enough: the
  // effect runs after the render that already used the new `user`, so a switch had
  // one frame showing the previous account's destination and dates. The render
  // compares ids instead, which cannot be a frame late.
  const [nextTripInfo, setNextTripInfo] =
    useState<{ userId: number; data: NextTripResponse } | null>(null);

  // One request, one answer. A failure leaves the board absent rather than
  // guessing — an invented countdown is worse than none.
  //
  // Signing out or switching accounts CLEARS it first. Returning early on a null
  // user left the previous account's trip on screen, and on a switch it stayed
  // until the replacement request came back — one account's destination and dates
  // shown to another.
  useEffect(() => {
    if (!user) {
      setNextTripInfo(null);
      return;
    }
    const requestedFor = user.id;
    let cancelled = false;
    getNextTrip()
      .then((r) => {
        // The account may have changed while this was in flight.
        if (!cancelled && useUserStore.getState().user?.id === requestedFor) {
          setNextTripInfo({ userId: requestedFor, data: r });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Only ever the current account's answer.
  const nextTrip = user && nextTripInfo?.userId === user.id ? nextTripInfo.data : null;

  // Event detail/edit modal state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // Focus target for the event sheet — see the dialog-behaviour effect below.
  const eventSheetRef = useRef<HTMLDivElement | null>(null);
  const [selectedExternalEvent, setSelectedExternalEvent] = useState<ScheduleEvent | null>(null);
  const [eventDetail, setEventDetail] = useState<Event | null>(null);
  const [eventDetailLoading, setEventDetailLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventData | null>(null);
  const [editFocus, setEditFocus] = useState<'time' | 'location' | undefined>(undefined);

  // Conflict resolution modal state

  // Day-rollover detection: dateKey changes when the local calendar date
  // changes (e.g. midnight crossing, or tab resumed the next day).
  // Added as a dependency to data-loading effects so they re-fetch.
  const [dateKey, setDateKey] = useState(() => toLocalDateStr(new Date()));

  // Bumped after a calendar is switched on or off in the settings sheet, which
  // opens over this page without unmounting it. The schedule effects below
  // list it so the day, week and month views refetch without that calendar.
  const calendarSettingsVersion = useSettingsStore((s) => s.calendarSettingsVersion);
  // The calendarSettingsVersion each Now event list was last loaded under
  // (null: nothing loaded yet). A failed refetch keeps the list on
  // screen only if it was loaded under the current version; one that predates
  // a calendar switch may still hold the switched-off calendar's events.
  const todayEventsVersionRef = useRef<number | null>(null);
  const viewedDayEventsVersionRef = useRef<number | null>(null);
  // Which Now list was cleared that way; the timeline shows its loading state
  // for it until a load succeeds.
  const [nowClearedFor, setNowClearedFor] = useState<'today' | 'viewed' | null>(null);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const currentDate = toLocalDateStr(new Date());
      setDateKey(prev => {
        if (prev !== currentDate) {
          // Day has rolled over — also reset offset to today
          setOffset(0);
          return currentDate;
        }
        return prev;
      });
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const firstName = user?.first_name || user?.email?.split('@')[0] || '';

  // === Scope navigation helpers ===
  const startOfWeek = (d: Date): Date => {
    const result = new Date(d);
    const day = result.getDay(); // 0=Sun
    const diff = day === 0 ? -6 : 1 - day; // Monday start
    result.setDate(result.getDate() + diff);
    result.setHours(0, 0, 0, 0);
    return result;
  };

  const addDays = (d: Date, n: number): Date => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  };

  const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

  const monthDiff = (a: Date, b: Date): number =>
    (a.getFullYear() - b.getFullYear()) * 12 + (a.getMonth() - b.getMonth());

  const dayDiff = (a: Date, b: Date): number =>
    Math.round((a.getTime() - b.getTime()) / 86400000);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Pod Today — check in-progress plans for Pod Today activation
  const inProgressPlanIds = todayPlanActivities
    .map(a => a.plan_id)
    .filter((v, i, arr) => arr.indexOf(v) === i);
  const viewedDateStr = toLocalDateStr(addDays(today, offset));
  const podToday = usePodToday(
    scope === 'today' && offset === 0 ? inProgressPlanIds : [],
    viewedDateStr,
  );

  // Scope anchor: the reference date for the current view
  const getScopeAnchor = (): Date => {
    if (scope === 'today') return addDays(today, offset);
    if (scope === 'week') return addDays(startOfWeek(today), offset * 7);
    return startOfMonth(new Date(today.getFullYear(), today.getMonth() + offset, 1));
  };

  const switchScope = (newScope: 'today' | 'week' | 'month') => {
    if (newScope === scope) return;
    const anchor = getScopeAnchor();
    let newOffset: number;
    if (newScope === 'today') {
      // Product decision: "Today" tab always jumps to actual today (override spec anchor-preserving)
      newOffset = 0;
    } else if (newScope === 'week') {
      newOffset = Math.floor(dayDiff(startOfWeek(anchor), startOfWeek(today)) / 7);
    } else {
      newOffset = monthDiff(anchor, today);
    }
    setScope(newScope);
    setOffset(newOffset);
    setPlanFilter('all');
  };

  // Date labels
  const viewedDate = getScopeAnchor();
  const dateLabel = scope === 'today'
    ? viewedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : scope === 'week'
      ? (() => {
          const ws = startOfWeek(addDays(today, 0));
          const weekStart = addDays(ws, offset * 7);
          const weekEnd = addDays(weekStart, 6);
          return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        })()
      : viewedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Fetch week data when scope changes to week
  useEffect(() => {
    if (scope !== 'week' || !user) return;
    // A superseded request (offset, date or calendar settings changed while it
    // was in flight) must not write its response, error or loading state.
    let cancelled = false;
    const ws = addDays(startOfWeek(today), offset * 7);
    setWeekLoading(true);
    getWeekActivities(toLocalDateStr(ws))
      .then(d => { if (!cancelled) setWeekData(d.days); })
      .catch(() => { if (!cancelled) setWeekData([]); })
      .finally(() => { if (!cancelled) setWeekLoading(false); });
    return () => { cancelled = true; };
  }, [scope, offset, user, dateKey, calendarSettingsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weather code to emoji (matches backend _weather_code_to_emoji)
  const weatherEmoji = (code: number | null | undefined): string => {
    if (code == null) return '☀️';
    if (code >= 1000 && code <= 1100) return '☀️';
    if ((code >= 1101 && code <= 1102) || code === 1001) return '⛅';
    if (code >= 2000 && code <= 2100) return '🌫️';
    if (code >= 4000 && code <= 4201) return '🌧️';
    if (code >= 5000 && code <= 5101) return '❄️';
    if (code >= 6000 && code <= 7102) return '🌨️';
    if (code === 8000) return '⛈️';
    return '⛅';
  };

  // Strip leading zeros from times: "03:00 PM" → "3:00 PM"
  const normalizeTime = (t: string | undefined | null): string => {
    if (!t) return '';
    return t.replace(/^0(\d)/, '$1');
  };

  // Shared mapping: briefing event → ScheduleEvent (reused for both offset paths)
  const briefingEventToScheduleEvent = (e: BriefingEvent): ScheduleEvent => ({
    id: e.id,
    time: normalizeTime(e.time_display || e.time),
    timeRaw: e.time,
    endTime: e.end_time_display || e.end_time,
    endTimeRaw: e.end_time,
    title: e.title,
    memberName: e.member_name,
    location: e.location,
    isPast: e.is_past,
    isCurrent: e.is_current,
    source: e.source,
    planName: e.plan_name,
    planId: e.plan_id,
    itemId: e.itinerary_item_id ?? null,
    activityStatus: (e.activity_status as 'pending' | 'completed' | 'skipped') ?? null,
    latitude: e.latitude ?? null,
    longitude: e.longitude ?? null,
    eventStatus: e.event_status ?? null,
  });

  // Refetch data when offset changes in today scope
  useEffect(() => {
    if (scope !== 'today' || !user) return;
    let cancelled = false;
    const viewedDateStr = toLocalDateStr(addDays(today, offset));
    const requestVersion = calendarSettingsVersion;
    const shownVersionRef = offset === 0 ? todayEventsVersionRef : viewedDayEventsVersionRef;

    // Fetch plan-specific data (tiers, weather, plan pins)
    getTodayActivities(viewedDateStr).then(data => {
      if (cancelled) return;
      const activities = data.activities || [];
      setActivityStatuses(new Map(activities.map(a => [String(a.id), a.activity_status])));
      setTodayPlanActivities(activities);
      setTodayWeather(data.weather || null);
    }).catch(() => {});

    // Fetch the daily briefing for the viewed date to get ALL events
    // (calendar + plan), not just plan activities. This is the single
    // source of truth for what's on the timeline at any offset.
    // Retry once on failure since cold-start backends sometimes time out.
    const fetchBriefingWithRetry = async () => {
      try {
        const data = await getDailyBriefing(viewedDateStr);
        return data;
      } catch (err) {
        // Wait 1.5s and retry once
        await new Promise(r => setTimeout(r, 1500));
        try {
          return await getDailyBriefing(viewedDateStr);
        } catch (err2) {
          console.error('[MobileHome] briefing fetch failed after retry', err2);
          return null;
        }
      }
    };

    fetchBriefingWithRetry().then(briefingData => {
      if (cancelled) return;
      if (briefingData?.events) {
        const events = briefingData.events
          .map(briefingEventToScheduleEvent)
          .sort((a, b) => (a.timeRaw || a.time || '').localeCompare(b.timeRaw || b.time || ''));
        if (offset === 0) {
          // Today: write to schedule store so the timeline picks it up
          schedule.setTodayBriefing(
            viewedDateStr,
            events,
            briefingData.weather?.summary || null,
            briefingData.weather_code ?? null,
            briefingData.weather_high ?? null,
          );
        } else {
          // Other days: write to viewedDayEvents
          setViewedDayEvents(events);
        }
      } else if (briefingData) {
        // Briefing returned but no events
        if (offset === 0) {
          schedule.setTodayBriefing(viewedDateStr, [], null, null, null);
        } else {
          setViewedDayEvents([]);
        }
      }
      if (briefingData) {
        shownVersionRef.current = requestVersion;
        setNowClearedFor(null);
      } else if (shownVersionRef.current !== null && shownVersionRef.current !== requestVersion) {
        // Failed after a calendar switch, and the list on screen was loaded
        // before it: it may hold the switched-off calendar's events. Clear it
        // and show the timeline's loading state instead.
        if (offset === 0) {
          schedule.setTodayBriefing(viewedDateStr, [], null, null, null);
        } else {
          setViewedDayEvents([]);
        }
        setNowClearedFor(offset === 0 ? 'today' : 'viewed');
      }
      // Otherwise (fetch failed even after retry, no calendar switch since the
      // list on screen was loaded), do NOT overwrite existing events — leave
      // whatever the previous render had, so a transient error does not flash
      // the timeline. The user can manually refresh if needed.

      // Use briefing weather for this date (home location — destination-aware is a follow-up)
      if (briefingData?.weather_high != null) {
        setTodayWeather({
          icon: weatherEmoji(briefingData.weather_code),
          high: Math.round(briefingData.weather_high),
          low: briefingData.weather_low != null ? Math.round(briefingData.weather_low) : null,
          description: '',
        });
      } else if (briefingData && offset !== 0) {
        setTodayWeather(null);
      }
    });

    return () => { cancelled = true; };
  }, [scope, offset, user, dateKey, calendarSettingsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch month data when scope changes to month
  useEffect(() => {
    if (scope !== 'month' || !user) return;
    const monthDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const monthStr = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
    // As for week: a superseded request writes nothing.
    let cancelled = false;
    setMonthLoading(true);
    getMonthSummary(monthStr)
      .then(d => { if (!cancelled) { setMonthData(d.weeks); setMonthClusters(d.clusters || []); } })
      .catch(() => { if (!cancelled) { setMonthData([]); setMonthClusters([]); } })
      .finally(() => { if (!cancelled) setMonthLoading(false); });
    return () => { cancelled = true; };
  }, [scope, offset, user, dateKey, calendarSettingsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch plans for destination-tap discovery sheet
  useEffect(() => {
    if (!discoverDestination) { setDiscoverPlans([]); return; }
    fetchApi<{ results: Array<{ id: number; title: string; slug: string; destination: string; used_count: number; creator_name: string; source?: string }> }>(
      `/api/v1/plans/discover/?destination=${encodeURIComponent(discoverDestination)}`
    ).then(d => setDiscoverPlans((d.results || []).slice(0, 5))).catch(() => setDiscoverPlans([]));
  }, [discoverDestination]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPast = scope === 'today' ? offset < 0 : false;
  const isFuture = scope === 'today' ? offset > 0 : false;

  // Header greeting
  const headerGreeting = scope === 'today'
    ? (offset === 0
        ? `Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}${firstName ? ', ' + firstName : ''}`
        : offset === -1 ? 'Yesterday'
        : isPast && offset >= -7 ? `Last ${viewedDate.toLocaleDateString('en-US', { weekday: 'long' })}`
        : isPast ? viewedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
        : offset === 1 ? 'Tomorrow'
        : 'Looking ahead')
    : scope === 'week'
      ? (offset === 0 ? 'This week' : offset === 1 ? 'Next week' : offset === -1 ? 'Last week' : offset > 0 ? 'Future week' : 'Past week')
      : (offset === 0 ? 'This month' : offset === 1 ? 'Next month' : offset === -1 ? 'Last month' : offset > 0 ? 'Future month' : 'Past month');

  // Generate greeting based on time
  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Generate proactive greeting based on time of day and context
  const generateProactiveMessage = useCallback((
    eventsData: ScheduleEvent[],
    conflictsData: EventConflict[],
    weatherStr: string | null
  ): ChatMessage => {
    const greeting = getGreeting();

    let content = `${greeting}, ${firstName}! `;

    if (weatherStr) {
      content += `${weatherStr} `;
    }

    if (eventsData.length === 0) {
      content += '\n\nYour day is clear. Want to plan something?';
    } else {
      const upcoming = eventsData.filter(e => !e.isPast);
      if (upcoming.length === 0) {
        content += `\n\nAll ${eventsData.length} events done for today!`;
      } else {
        const nextEvent = upcoming[0];
        content += `\n\nNext up: ${nextEvent.title} at ${nextEvent.time}`;
        if (nextEvent.location) content += ` @ ${nextEvent.location}`;
        content += '.';
      }
    }

    return {
      id: 'proactive-' + Date.now(),
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      isProactive: true,
    };
  }, [firstName]);

  // Fetch briefing with one retry on failure
  const fetchBriefingWithRetry = useCallback(async (dateStr: string) => {
    const result = await safeAsync(getDailyBriefing(dateStr), null, 'getDailyBriefing');
    if (result) return result;
    // One retry after a short delay
    await new Promise(r => setTimeout(r, 2000));
    return safeAsync(getDailyBriefing(dateStr), null, 'getDailyBriefing (retry)');
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    schedule.setIsLoading(true);
    schedule.setBriefingFailed(false);
    try {
      const todayStr = schedule.getTodayString();
      const [briefingData] = await Promise.all([
        fetchBriefingWithRetry(todayStr),
        fetchConflicts(),
      ]);

      // Fetch gap suggestions + incoming shares (non-blocking)
      // Note: plan activities, trip tiers, and weather are fetched by the
      // offset-aware effect below (scope === 'today' + offset dependency)
      getTodayGaps().then(data => setGaps(data.gaps || [])).catch(() => {});
      getIncomingShares().then(data => setIncomingShares(data.shares || [])).catch(() => {});

      // Read conflicts from store for proactive message
      const allConflicts = useEventsStore.getState().conflicts || [];
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const upcomingConflicts = allConflicts.filter((conflict: EventConflict) => {
        const overlapStart = new Date(conflict.overlap_start);
        return overlapStart >= now && overlapStart <= sevenDaysFromNow;
      });

      let loadedEvents: ScheduleEvent[] = [];
      if (briefingData) {
        // Note: events are written to the schedule store by the offset-aware
        // Today effect (above). loadData() reads them here only to seed the
        // proactive chat message and extract trip_mode.upcoming_trip.
        loadedEvents = briefingData.events
          .map(briefingEventToScheduleEvent)
          .sort((a: ScheduleEvent, b: ScheduleEvent) => (a.timeRaw || a.time || '').localeCompare(b.timeRaw || b.time || ''));

      } else {
        schedule.setBriefingFailed(true);
      }

      if (!chat.hasMessages) {
        const proactiveMsg = generateProactiveMessage(
          loadedEvents,
          upcomingConflicts,
          briefingData?.weather?.summary || null
        );
        chat.addProactiveMessage(proactiveMsg);
      }
      hasLoadedOnce.current = true;
    } catch (err) {
      console.error('Failed to load data:', err);
      schedule.setBriefingFailed(true);
    } finally {
      schedule.setIsLoading(false);
    }
  }, [chat.hasMessages, generateProactiveMessage, fetchBriefingWithRetry]);

  useEffect(() => {
    if (user && !hasLoadedOnce.current) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Surface events store errors
  useEffect(() => {
    if (eventsStoreError) {
      console.error('Events store error:', eventsStoreError);
    }
  }, [eventsStoreError]);

  // Event detail/edit modal handlers
  const handleEventClick = (eventId: string) => {
    // Find the event in schedule data to check its source
    const allEvents = schedule.events;
    const event = allEvents.find(e => e.id === eventId);
    const isExternal = event?.source === 'google' || event?.source === 'microsoft' || event?.source === 'subscribed' || eventId.startsWith('gcal-');

    if (isExternal && event) {
      setSelectedExternalEvent(event);
    } else {
      setSelectedEventId(eventId);
    }
  };

  const handleCloseEventDetail = () => {
    setSelectedEventId(null);
    setSelectedExternalEvent(null);
    setEventDetail(null);
    setEventDetailLoading(false);
  };

  // Dialog behaviour for the event sheet: Escape to close, focus moved in on
  // open and returned to the trigger on close.
  //
  // The sheet replaced EventDetailModal, which had role="dialog", aria-modal
  // and Escape handling plus a test asserting all three. The replacement
  // shipped without them, and the dead modal's test kept passing — so the
  // coverage existed for a component nothing rendered while the live surface
  // had none.
  //
  // Focus is the half that aria-modal cannot fake: it tells assistive tech the
  // rest of the page is inert, so leaving focus on the element behind the
  // sheet makes the announcement a lie and strands keyboard users outside a
  // dialog they cannot see.
  useEffect(() => {
    if (!selectedEventId && !selectedExternalEvent) return;

    const trigger = document.activeElement as HTMLElement | null;
    // After paint, so the sheet exists to receive focus.
    const raf = requestAnimationFrame(() => eventSheetRef.current?.focus());

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseEventDetail();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      // Return focus where it came from — closing a dialog should not dump
      // the user back at the top of the document.
      if (trigger && document.contains(trigger)) trigger.focus();
    };
  }, [selectedEventId, selectedExternalEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch full event details when popup opens for a vizday event
  useEffect(() => {
    if (!selectedEventId || selectedExternalEvent) {
      return;
    }
    let cancelled = false;
    setEventDetailLoading(true);
    setEventDetail(null);
    getEvent(selectedEventId)
      .then(data => {
        if (!cancelled) {
          setEventDetail(data);
          setEventDetailLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Failed to load event details:', err);
          setEventDetailLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [selectedEventId, selectedExternalEvent]);

  const handleEditEvent = async (focus?: 'time' | 'location') => {
    if (!selectedEventId) return;
    try {
      const eventData = await getEvent(selectedEventId);
      setSelectedEventId(null);
      setEditFocus(focus);
      setEditingEvent({ ...eventData, source: 'vizday' as const });
    } catch (err) {
      console.error('Failed to load event for editing:', err);
    }
  };

  const handleCloseEventEdit = () => {
    setEditingEvent(null);
  };

  const handleEventSaved = () => {
    setEditingEvent(null);
    clearScheduleCache();
    loadData();
  };

  const handleEventDeleted = () => {
    setEditingEvent(null);
    setSelectedEventId(null);
    clearScheduleCache();
    loadData();
  };

  return (
    <div className="mobile-home">
      {/* Full-Screen Chat Overlay */}
      <ChatOverlay chat={chat} onNavigate={(path) => navigate(path)} />

      {/* Main Content */}
      <div className="main-content">
        {user ? (
          <>
            {/* Incoming shares strip — renders above everything when present */}
            {incomingShares.length > 0 && scope === 'today' && offset === 0 && (
              <div className="today-shares-strip">
                {incomingShares.map((s, i) => {
                  const verb = s.type === 'fork_of_yours' ? 'used your' : s.type === 'suggestion_accepted' ? 'accepted your suggestion for' : s.type === 'vote_invite' ? 'invited you to vote on' : s.type === 'invite_joined' ? 'invited you to' : s.type === 'vote_digest' ? 'New votes on' : 'shared';
                  const timeAgo = (() => {
                    const diff = Date.now() - new Date(s.timestamp).getTime();
                    const hrs = Math.floor(diff / 3600000);
                    if (hrs < 1) return 'just now';
                    if (hrs < 24) return `${hrs}h ago`;
                    return `${Math.floor(hrs / 24)}d ago`;
                  })();
                  return (
                    <div key={i} className="today-share-card" onClick={() => navigate(`/itinerary/${s.plan_id}`)}>
                      <div className="today-share-avatar">{s.actor_initials}</div>
                      <div className="today-share-content">
                        <span className="today-share-text"><strong>{s.actor_name}</strong> {verb} <strong>{s.plan_title}</strong></span>
                        <span className="today-share-time">{timeAgo}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Header */}
            <div className="today-header">
              <div className="today-header-date">
                <span>{dateLabel}</span>
                {scope === 'today' && todayWeather && (
                  <span className="today-header-weather">
                    · {todayWeather.icon} {todayWeather.high}°{todayWeather.low != null ? ` / ${todayWeather.low}°` : ''}
                  </span>
                )}
                {scope === 'today' && !todayWeather && offset === 0 && schedule.weatherHigh !== null && (
                  <span className="today-header-weather">· ☀️ {schedule.weatherHigh}°</span>
                )}
              </div>
              <h2 className="today-header-greeting">{headerGreeting}</h2>
              {scope === 'today' && podToday.isActive && (
                <div className="today-pod-header">
                  <span className="today-pod-header-icon">👫</span>
                  <span className="today-pod-header-text">
                    {podToday.podNames.join(' + ')} — {podToday.planTitle}
                    {podToday.planDestination ? `, ${podToday.planDestination}` : ''}
                  </span>
                </div>
              )}
              {scope === 'today' && (() => {
                const headerEvents = offset === 0 ? schedule.events : viewedDayEvents;
                if (headerEvents.length === 0) return null;
                const planCount = new Set(headerEvents.filter(e => e.planName).map(e => e.planName)).size;
                return (
                  <p className="today-header-summary">
                    {headerEvents.length} activit{headerEvents.length === 1 ? 'y' : 'ies'}
                    {planCount > 0 ? ` · ${planCount} plan${planCount !== 1 ? 's' : ''}` : ''}
                  </p>
                );
              })()}
            </div>

            {/* Scope toggle */}
            <div className="today-scope-toggle">
              {(['today', 'week', 'month'] as const).map(s => (
                <button
                  key={s}
                  className={`today-scope-btn ${scope === s ? 'today-scope-btn--active' : ''}`}
                  onClick={() => switchScope(s)}
                >
                  {s === 'today' ? 'Today' : s === 'week' ? 'Week' : 'Month'}
                </button>
              ))}
            </div>

            {/* Date navigator */}
            <div className="today-date-nav">
              <button className="today-nav-arrow" onClick={() => setOffset(o => o - 1)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div className="today-nav-center">
                <span className="today-nav-label">{dateLabel}</span>
                {offset !== 0 && (
                  <button className="today-nav-jump" onClick={() => setOffset(0)}>
                    {scope === 'today' ? '← Today' : scope === 'week' ? '← This week' : '← This month'}
                  </button>
                )}
              </div>
              <button className="today-nav-arrow" onClick={() => setOffset(o => o + 1)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>

            {/* Scope-aware map */}
            {(() => {
              let mapMarkers: Array<{ id: string; lat: number; lng: number; label: string; sublabel?: string; color?: string }> = [];
              let mapMode: 'pins' | 'dots' | 'clusters' = 'pins';

              if (scope === 'today') {
                // Combine plan activities + calendar events that have geocodes
                const planPins = todayPlanActivities
                  .filter(a => a.latitude != null && a.longitude != null)
                  .map((a, i) => ({
                    id: `plan-${a.id}-${i}`,
                    lat: a.latitude!,
                    lng: a.longitude!,
                    label: a.name,
                    sublabel: a.location,
                    color: a.plan_name?.toLowerCase().includes('reset') ? 'var(--text-secondary)' : 'var(--accent-primary)',
                  }));
                const calEvents = offset === 0 ? schedule.events : viewedDayEvents;
                const calPins = calEvents
                  .filter(e => e.latitude != null && e.longitude != null && !e.planId)
                  .map((e, i) => ({
                    id: `cal-${e.id}-${i}`,
                    lat: e.latitude!,
                    lng: e.longitude!,
                    label: e.title,
                    sublabel: e.location || undefined,
                    color: 'var(--text-secondary)',
                  }));
                mapMarkers = [...planPins, ...calPins];
                mapMode = 'pins';
              } else if (scope === 'week') {
                mapMarkers = weekData.flatMap(day =>
                  day.activities
                    .filter((a: any) => a.latitude != null && a.longitude != null)
                    .map((a: any, i: number) => ({
                      id: `week-${day.date}-${a.id}-${i}`,
                      lat: a.latitude,
                      lng: a.longitude,
                      label: a.name,
                      sublabel: a.plan_name,
                      color: a.plan_name?.includes('Reset') ? 'var(--text-secondary)' : 'var(--accent-primary)',
                    }))
                );
                mapMode = 'dots';
              } else {
                mapMarkers = monthClusters.map((c, i) => ({
                  id: `month-${i}`,
                  lat: c.lat,
                  lng: c.lng,
                  label: c.destination,
                  sublabel: `${c.activity_count} activities`,
                }));
                mapMode = 'clusters';
              }

              if (mapMarkers.length === 0) {
                return (
                  <div className="today-map-container today-map-empty">
                    <span className="today-map-empty-text">No locations for this {scope === 'today' ? 'day' : scope}</span>
                    <span className="today-map-badge">VizDay Map</span>
                  </div>
                );
              }

              return (
                <div className="today-map-container">
                  <UnifiedMap markers={mapMarkers} mode={mapMode} onMarkerClick={(m) => setDiscoverDestination(m.label)} />
                </div>
              );
            })()}

            {/* Plan filter pills — Today scope */}
            {scope === 'today' && (() => {
              const dayEvents = offset === 0 ? schedule.events : viewedDayEvents;
              if (dayEvents.length === 0) return null;
              const plans = Array.from(new Set(dayEvents.filter(e => e.planName).map(e => e.planName!)));
              if (plans.length <= 1) return null;
              return (
                <div className="today-plan-filters">
                  {['all', ...plans].map(p => (
                    <button
                      key={p}
                      className={`today-plan-filter-pill ${planFilter === p ? 'today-plan-filter-pill--active' : ''}`}
                      onClick={() => setPlanFilter(p)}
                    >
                      {p === 'all' ? 'All plans' : p}
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* Exactly one authoritative trip surface, from one server payload. */}
            {scope === 'today' && nextTrip && (
              <DepartureBoard activeTrip={nextTrip.active_trip} nextTrip={nextTrip.next_trip} />
            )}

            {/* Today Timeline */}
            {scope === 'today' && (() => {
              const dayEvents = offset === 0 ? schedule.events : viewedDayEvents;
              const filteredEvents = planFilter === 'all' ? dayEvents : dayEvents.filter(e => e.planName === planFilter);
              return (
              <TodayTimeline
                events={filteredEvents}
                gaps={gaps}
                activityStatuses={activityStatuses}
                isPast={isPast}
                isFuture={isFuture}
                isLoading={schedule.isLoading || nowClearedFor === (offset === 0 ? 'today' : 'viewed')}
                onExplore={() => navigate('/explore')}
                onEventClick={(eventId) => {
                  const event = dayEvents.find(e => e.id === eventId);
                  if (event?.planId) {
                    navigate(`/itinerary/${event.planId}`);
                  } else {
                    handleEventClick(eventId);
                  }
                }}
                onCheckin={async (itemIdStr, planId, currentStatus) => {
                  const cycle: Record<string, string> = { pending: 'completed', completed: 'skipped', skipped: 'pending' };
                  const newStatus = cycle[currentStatus] || 'completed';
                  const itemId = parseInt(itemIdStr);
                  if (isNaN(itemId)) return;
                  try {
                    await checkinItineraryItem(planId, itemId, newStatus as 'completed' | 'skipped' | 'pending');
                    setActivityStatuses(prev => new Map(prev).set(itemIdStr, newStatus));
                  } catch { /* silent */ }
                }}
                onAddEvent={() => {
                  chat.setIsChatExpanded(true);
                  setTimeout(() => {
                    chat.setInputValue('Add an event ');
                    chat.inputRef.current?.focus();
                  }, 300);
                }}
              />
              );
            })()}

            {/* Week View */}
            {scope === 'week' && (
              <div className="today-week-view">
                {weekLoading && <div className="today-week-loading">Loading week...</div>}
                {!weekLoading && weekData.map((day, di) => (
                  <div key={day.date} className="today-week-day">
                    <div className="today-week-day-header" onClick={() => {
                      const dayDate = new Date(day.date + 'T00:00:00');
                      setScope('today');
                      setOffset(dayDiff(dayDate, today));
                      setPlanFilter('all');
                    }} style={{ cursor: 'pointer' }}>
                      <span className="today-week-day-name">{day.day_name} · {day.day_label}</span>
                      {day.date === toLocalDateStr(today) && (
                        <span className="today-week-day-today-dot" />
                      )}
                      {day.weather && (
                        <span className="today-week-day-weather">{day.weather.icon} {day.weather.high}° / {day.weather.low}°</span>
                      )}
                      <span className="today-week-day-count">{day.activity_count} activities</span>
                    </div>
                    {day.activities.map((a, ai) => (
                      <div key={ai} className="today-week-activity" onClick={() => a.plan_id && navigate(`/itinerary/${a.plan_id}`)}>
                        <span className="today-week-act-time">{a.time || ''}</span>
                        <div className="today-week-act-dot" style={{ background: a.plan_name?.includes('Reset') ? 'var(--color-sage)' : 'var(--accent-primary)' }} />
                        <div className="today-week-act-card">
                          <span className="today-week-act-name">{a.name}</span>
                          {a.location && <span className="today-week-act-loc">{a.location}</span>}
                          <span className="today-week-act-plan">{a.plan_name}</span>
                        </div>
                      </div>
                    ))}
                    {day.activity_count === 0 && (
                      <div className="today-week-empty">No activities</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Month View */}
            {scope === 'month' && (
              <div className="today-month-view">
                {monthLoading && <div className="today-week-loading">Loading month...</div>}
                {!monthLoading && monthData.map((w, wi) => (
                  <div key={wi} className="today-month-week" onClick={() => { switchScope('week'); setOffset(wi); }}>
                    <div className="today-month-week-header">
                      <div>
                        <span className="today-month-week-label">{w.dates_label}</span>
                      </div>
                      <span className="today-month-week-count">{w.activity_count} activities</span>
                    </div>
                    {w.top_destination && (
                      <div className="today-month-week-dest">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--accent-primary)" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
                        <span>{w.top_destination}</span>
                        {w.plans.length > 0 && <span className="today-month-week-plans">{w.plans.join(' · ')}</span>}
                      </div>
                    )}
                    {w.activity_count === 0 && !w.top_destination && (
                      <div className="today-week-empty">Open week</div>
                    )}
                  </div>
                ))}
                {!monthLoading && monthData.length === 0 && (
                  <div className="today-month-placeholder">No activities this month</div>
                )}
              </div>
            )}

            {/* Post-checkin share nudge — only for days with plan activities that were checked in */}
            {scope === 'today' && offset === 0 && schedule.events.some(e => e.planId && e.isPast) && (
              <div className="today-checkin-nudge">
                <span className="today-checkin-nudge-emoji">🎉</span>
                <div className="today-checkin-nudge-content">
                  <span className="today-checkin-nudge-title">Wrapping up your day?</span>
                  <span className="today-checkin-nudge-desc">Share what you loved.</span>
                </div>
                <button className="today-checkin-nudge-btn" onClick={() => {
                  // Open share sheet for the first plan
                  const planId = schedule.events[0]?.planId;
                  if (planId) navigate(`/itinerary/${planId}`);
                }}>Share day</button>
              </div>
            )}

            {/* Empty state for Today is now handled by TodayTimeline */}

            {/* Pod Today: Day Feed Drawer */}
            {scope === 'today' && podToday.isActive && podToday.activePlanId && (
              <DayFeedDrawer
                planId={podToday.activePlanId}
                date={viewedDateStr}
                refreshKey={podToday.feedRefreshKey}
              />
            )}

          </>
        ) : (
          /* Anonymous first screen: the question and up to three starters */
          <FirstScreen
            onAsk={() => {
              planChat.reset({ intent: 'new_plan', entryId: newUuid() });
              setIsPlanChatOpen(true);
            }}
          />
        )}
      </div>

      {/* Plan Chat Overlay for anonymous users */}
      <PlanChatOverlay
        chat={planChat}
        isExpanded={isPlanChatOpen}
        onClose={() => setIsPlanChatOpen(false)}
        onNavigate={(path) => navigate(path)}
      />

      {/* Bottom Navigation */}
      {/* Destination-tap discovery sheet */}
      {discoverDestination && (
        <div className="today-discover-backdrop" onClick={() => setDiscoverDestination(null)}>
          <div className="today-discover-sheet" onClick={e => e.stopPropagation()}>
            <div className="today-discover-handle" />
            <h3 className="today-discover-title">{discoverDestination}</h3>
            <p className="today-discover-sub">{discoverPlans.length > 0 ? `${discoverPlans.length} plan${discoverPlans.length !== 1 ? 's' : ''} found` : `No plans for ${discoverDestination} yet`}</p>
            {discoverPlans.map(p => {
              const isUserPlan = p.source === 'user';
              // Joined from parts: no dangling separator, and no count at 0 uses.
              const meta = [
                p.creator_name ? `by ${p.creator_name}` : '',
                p.used_count > 0 ? `used by ${p.used_count}` : '',
              ].filter(Boolean).join(' · ');
              return (
                <div key={`${p.source || 'c'}-${p.id}`} className="today-discover-plan" onClick={() => { setDiscoverDestination(null); navigate(isUserPlan ? `/itinerary/${p.id}` : `/plans/p/${p.slug}`); }}>
                  <div className="today-discover-plan-info">
                    <span className="today-discover-plan-title">{p.title}</span>
                    {meta && <span className="today-discover-plan-meta">{meta}</span>}
                  </div>
                  <button className="today-discover-plan-fork" onClick={(e) => {
                    e.stopPropagation();
                    setDiscoverDestination(null);
                    navigate(isUserPlan ? `/itinerary/${p.id}` : `/plans/p/${p.slug}`);
                  }}>
                    View & Save
                  </button>
                </div>
              );
            })}
            <button className="today-discover-explore" onClick={() => { setDiscoverDestination(null); navigate(`/explore?search=${encodeURIComponent(discoverDestination)}`); }}>
              See all in Explore →
            </button>
          </div>
        </div>
      )}

      <BottomNav activeItem="today" />

      {/* Event Detail Bottom Sheet (all event types from Today) */}
      {(selectedEventId || selectedExternalEvent) && (() => {
        // Use offset-aware event list to find the event
        const allDayEvents = offset === 0 ? schedule.events : viewedDayEvents;
        const evt = selectedExternalEvent || allDayEvents.find(e => e.id === selectedEventId);
        if (!evt) return null;
        const isVizday = !selectedExternalEvent && evt.source === 'vizday';
        const dur = (() => {
          if (!evt.timeRaw || !evt.endTimeRaw) return null;
          try {
            const s = new Date(evt.timeRaw).getTime();
            const e = new Date(evt.endTimeRaw).getTime();
            if (isNaN(s) || isNaN(e)) return null;
            const m = (e - s) / 60000;
            if (m <= 0 || m > 1440) return null;
            if (m < 60) return `${Math.round(m)} min`;
            const hrs = Math.floor(m / 60);
            const mins = Math.round((m % 60) / 5) * 5;
            if (mins === 0) return `${hrs} hr`;
            return `${hrs} hr ${mins} min`;
          } catch { return null; }
        })();
        const planColor = evt.planName?.toLowerCase().includes('reset') || evt.planName?.toLowerCase().includes('routine')
          ? 'var(--color-sage)'
          : 'var(--accent-primary)';

        // Pull rich fields from full event detail (loaded async) or fall back to ScheduleEvent
        const description = eventDetail?.description?.trim() || '';
        const address = eventDetail?.address?.trim() || evt.address?.trim() || '';
        const providerName = eventDetail?.provider_name?.trim() || '';
        const providerPhone = eventDetail?.provider_phone?.trim() || '';
        const confirmationNumber = eventDetail?.confirmation_number?.trim() || '';
        const guardianNote = evt.guardianNote?.trim() || '';
        const contextNotes = (evt.contextNotes || []).filter(n => n && n.trim());
        const showAddress = address && address !== evt.location;
        const mapsUrl = address || evt.location
          ? `https://maps.google.com/?q=${encodeURIComponent(address || evt.location || '')}`
          : null;

        // Member display: prefer full Event family_members (vizday), fall back to briefing memberName.
        // Suppress the current user from both paths — "You" is implicit on your own Today screen.
        const selfNames = new Set(
          [user?.first_name, 'You']
            .filter((n): n is string => !!n)
            .map(n => n.toLowerCase())
        );
        const isSelf = (name: string | null | undefined) => !!name && selfNames.has(name.toLowerCase());
        const fullMemberNames = (eventDetail?.family_members || [])
          .map(m => m.name)
          .filter(n => n && !isSelf(n));
        const memberDisplay = fullMemberNames.length > 0
          ? fullMemberNames.join(', ')
          : (evt.memberName && !isSelf(evt.memberName) ? evt.memberName : '');

        // Calendar source: external events have calendarName from briefing,
        // vizday events show "VizDay" by default
        const calendarName = evt.calendarName || (evt.source === 'vizday' ? 'VizDay' : null);
        const calendarColor = evt.calendarColor || 'var(--accent-primary)';

        return (
          <div className="today-event-sheet-overlay" onClick={handleCloseEventDetail}>
            <div
              ref={eventSheetRef}
              className="today-event-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={evt.title || 'Event details'}
              // Programmatically focusable, but not a tab stop of its own.
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="today-event-sheet-handle" />
              <div className="today-event-sheet-header">
                <div>
                  <p className="today-event-sheet-title">{evt.title}</p>
                  <p className="today-event-sheet-meta">
                    {evt.time || ''}{dur ? ` · ${dur}` : ''}
                    {eventDetailLoading && <span className="today-event-sheet-loading-inline"> · loading details…</span>}
                  </p>
                </div>
                {evt.planName && (
                  <span className="today-event-sheet-badge" style={{ color: planColor, background: `${planColor}18` }}>
                    {evt.planName}
                  </span>
                )}
              </div>

              {description && (
                <p className="today-event-sheet-desc">{description}</p>
              )}

              {evt.location && (
                <div className="today-event-sheet-row">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--accent-primary)" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
                  <div className="today-event-sheet-row-content">
                    <span>{evt.location}</span>
                    {showAddress && <span className="today-event-sheet-row-sub">{address}</span>}
                    {mapsUrl && (
                      <a className="today-event-sheet-row-link" href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Open in Maps</a>
                    )}
                  </div>
                </div>
              )}

              {memberDisplay && (
                <div className="today-event-sheet-row">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                  <div className="today-event-sheet-row-content">
                    <span>{memberDisplay}</span>
                  </div>
                </div>
              )}

              {calendarName && (
                <div className="today-event-sheet-row">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                  <div className="today-event-sheet-row-content">
                    <span className="today-event-sheet-calendar">
                      <span className="today-event-sheet-calendar-dot" style={{ background: calendarColor }} />
                      {calendarName}
                    </span>
                  </div>
                </div>
              )}

              {guardianNote && (
                <div className="today-event-sheet-row today-event-sheet-row--accent">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                  <div className="today-event-sheet-row-content">
                    <span>{guardianNote}</span>
                  </div>
                </div>
              )}

              {contextNotes.length > 0 && (
                <div className="today-event-sheet-notes">
                  {contextNotes.map((note, i) => (
                    <div key={i} className="today-event-sheet-note">
                      <span className="today-event-sheet-note-bullet">•</span>
                      <span>{note}</span>
                    </div>
                  ))}
                </div>
              )}

              {(providerName || providerPhone || confirmationNumber) && (
                <div className="today-event-sheet-provider">
                  {providerName && (
                    <div className="today-event-sheet-provider-row">
                      <span className="today-event-sheet-provider-label">Provider</span>
                      <span>{providerName}</span>
                    </div>
                  )}
                  {providerPhone && (
                    <div className="today-event-sheet-provider-row">
                      <span className="today-event-sheet-provider-label">Phone</span>
                      <a href={`tel:${providerPhone}`}>{providerPhone}</a>
                    </div>
                  )}
                  {confirmationNumber && (
                    <div className="today-event-sheet-provider-row">
                      <span className="today-event-sheet-provider-label">Confirmation</span>
                      <span>{confirmationNumber}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="today-event-sheet-actions">
                {isVizday && (
                  <>
                    <button className="today-event-sheet-btn" onClick={() => { handleCloseEventDetail(); handleEditEvent('time'); }}>Edit time</button>
                    <button className="today-event-sheet-btn" onClick={() => { handleCloseEventDetail(); handleEditEvent('location'); }}>Edit location</button>
                  </>
                )}
                <button className="today-event-sheet-btn today-event-sheet-btn--done" onClick={handleCloseEventDetail}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Event Edit Modal */}
      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          onClose={() => { handleCloseEventEdit(); setEditFocus(undefined); }}
          onSave={() => { handleEventSaved(); setEditFocus(undefined); }}
          onDelete={handleEventDeleted}
          initialFocus={editFocus}
        />
      )}

      {/* Pod Today: Onboarding overlay (first encounter) */}
      {podToday.isActive && (
        <PodTodayOnboarding
          podName={podToday.podNames[0] || 'Your Pod'}
          planTitle={podToday.planTitle}
          onDismiss={() => {}}
        />
      )}

      {/* Pod Today: Update Composer */}
      {updateComposerTarget && (
        <UpdateComposer
          activityName={updateComposerTarget.name}
          onPost={async (type, text, dur) => {
            await podToday.postUpdate(updateComposerTarget.itemId, type, text, dur);
          }}
          onClose={() => setUpdateComposerTarget(null)}
        />
      )}

    </div>
  );
};

export default MobileHome;
